/**
 * Gibbs free energy: ΔG = ΔH − TΔS, the temperature at which it changes sign, and its
 * link to the equilibrium constant, ΔG° = −RT ln K, and the reaction quotient,
 * ΔG = ΔG° + RT ln Q.
 *
 * Working units are joules per mole, joules per mole-kelvin and kelvin. Energy and
 * entropy units are converted on the way in, which removes the classic error of
 * subtracting kJ/mol from J/(mol·K). Equilibrium constants are carried as log₁₀ K so
 * that a K of 10⁴⁰⁰ is reported rather than overflowing to infinity.
 */

import type { UnitDef } from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { CALORIE, GAS_CONSTANT } from "./constants";
import { type HeatFailure, type ReadSpec, readAll, unitFactor } from "./heat";
import { fromKelvin } from "./units";

export const MOLAR_ENERGY_UNITS: readonly UnitDef[] = [
  { id: "kJ", symbol: "kJ/mol", factor: 1000 },
  { id: "J", symbol: "J/mol", factor: 1 },
  { id: "kcal", symbol: "kcal/mol", factor: CALORIE * 1000 },
  { id: "cal", symbol: "cal/mol", factor: CALORIE },
];

export const MOLAR_ENTROPY_UNITS: readonly UnitDef[] = [
  { id: "J", symbol: "J/(mol·K)", factor: 1 },
  { id: "kJ", symbol: "kJ/(mol·K)", factor: 1000 },
  { id: "cal", symbol: "cal/(mol·K)", factor: CALORIE },
];

export type GibbsFailure =
  | HeatFailure
  | { ok: false; reason: "zeroEntropy" | "badConstant" };

/* ----------------------------------------------------- ΔG = ΔH − TΔS */

export type GibbsTerm = "g" | "h" | "s" | "t";
export const GIBBS_TERMS: readonly GibbsTerm[] = ["g", "h", "s", "t"];

export interface GibbsInput {
  unknown: GibbsTerm;
  text: Record<GibbsTerm, string>;
  /** Unit ids: energy (ΔG and ΔH each), entropy, temperature. */
  unit: { g: string; h: string; s: string; t: string };
}

export type SpontaneityCase = "allT" | "lowT" | "highT" | "never" | "zero";
export type Spontaneity = "spontaneous" | "nonSpontaneous" | "equilibrium";

export interface GibbsSolution {
  ok: true;
  unknown: GibbsTerm;
  /** J/mol, J/mol, J/(mol·K), K */
  values: Record<GibbsTerm, number>;
  answer: number;
  answerUnit: string;
  spontaneity: Spontaneity;
  /** Sign pattern of ΔH and ΔS. */
  pattern: SpontaneityCase;
  /** ΔH ÷ ΔS in kelvin when it is positive; null when ΔG never changes sign. */
  crossover: number | null;
  /** log₁₀ K if these are standard values (ΔG° = ΔG). */
  log10K: number;
}

export type GibbsOutcome = GibbsSolution | GibbsFailure;

const EQUILIBRIUM_TOLERANCE = 1e-9;

export function spontaneityCase(h: number, s: number): SpontaneityCase {
  if (h === 0 && s === 0) {
    return "zero";
  }
  if (h <= 0 && s >= 0) {
    return "allT";
  }
  if (h >= 0 && s <= 0) {
    return "never";
  }
  return h < 0 ? "lowT" : "highT";
}

export function classify(
  g: number,
  h: number,
  s: number,
  t: number
): Spontaneity {
  const scale = Math.max(Math.abs(h), Math.abs(t * s), 1);
  if (Math.abs(g) <= EQUILIBRIUM_TOLERANCE * scale) {
    return "equilibrium";
  }
  return g < 0 ? "spontaneous" : "nonSpontaneous";
}

function gibbsFormula(
  unknown: GibbsTerm,
  v: Record<string, number>
): { ok: true; value: number } | GibbsFailure {
  if (unknown === "g") {
    return { ok: true, value: v.h - v.t * v.s };
  }
  if (unknown === "h") {
    return { ok: true, value: v.g + v.t * v.s };
  }
  if (unknown === "s") {
    return { ok: true, value: (v.h - v.g) / v.t };
  }
  if (v.s === 0) {
    return { ok: false, reason: "zeroEntropy" };
  }
  const t = (v.h - v.g) / v.s;
  return t > 0
    ? { ok: true, value: t }
    : { ok: false, reason: "resultBelowZero" };
}

export function log10KFromDeltaG(gJoules: number, kelvin: number): number {
  return -gJoules / (GAS_CONSTANT * kelvin * Math.LN10);
}

export function solveGibbs(input: GibbsInput): GibbsOutcome {
  const { unknown, unit } = input;
  const factors: Record<GibbsTerm, number> = {
    g: unitFactor(MOLAR_ENERGY_UNITS, unit.g),
    h: unitFactor(MOLAR_ENERGY_UNITS, unit.h),
    s: unitFactor(MOLAR_ENTROPY_UNITS, unit.s),
    t: 1,
  };
  const read = readAll(
    GIBBS_TERMS.filter((term) => term !== unknown).map((term) => ({
      key: term,
      text: input.text[term],
      kind: term === "t" ? ("temperature" as const) : ("any" as const),
      unit: term === "t" ? unit.t : "",
      factor: factors[term],
    }))
  );
  if ("ok" in read) {
    return read;
  }
  const solved = gibbsFormula(unknown, read.values);
  if (solved.ok === false) {
    return solved;
  }
  const values = { ...read.values, [unknown]: solved.value } as Record<
    GibbsTerm,
    number
  >;
  const answer =
    unknown === "t"
      ? fromKelvin(values.t, unit.t)
      : values[unknown] / factors[unknown];
  const ratio = values.s === 0 ? Number.NaN : values.h / values.s;
  return {
    ok: true,
    unknown,
    values,
    answer,
    answerUnit: unit[unknown],
    spontaneity: classify(values.g, values.h, values.s, values.t),
    pattern: spontaneityCase(values.h, values.s),
    crossover: ratio > 0 && Number.isFinite(ratio) ? ratio : null,
    log10K: log10KFromDeltaG(values.g, values.t),
  };
}

/* ------------------------------------------------- ΔG°, K and Q */

export type EquilibriumMode = "k" | "dg0" | "dg";

export interface EquilibriumInput {
  mode: EquilibriumMode;
  text: { dg0: string; k: string; q: string; t: string };
  unit: { dg0: string; dg: string; t: string };
}

export type Direction = "forward" | "reverse" | "equilibrium";

export interface EquilibriumSolution {
  ok: true;
  mode: EquilibriumMode;
  /** J/mol */
  dg0: number;
  kelvin: number;
  /** log₁₀ K and ln K. */
  log10K: number;
  lnK: number;
  /** Present for the ΔG mode. */
  q: number | null;
  dg: number | null;
  direction: Direction | null;
  /** The solved number: K, ΔG° (in the chosen unit) or ΔG (in the chosen unit). */
  answer: number;
  answerUnit: string;
}

export type EquilibriumOutcome = EquilibriumSolution | GibbsFailure;

function readPositive(
  key: string,
  text: string
): { value: number } | GibbsFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", key };
  }
  return typed > 0
    ? { value: typed }
    : { ok: false, reason: "nonPositive", key };
}

function directionOf(dg: number, scale: number): Direction {
  if (Math.abs(dg) <= EQUILIBRIUM_TOLERANCE * Math.max(scale, 1)) {
    return "equilibrium";
  }
  return dg < 0 ? "forward" : "reverse";
}

export function solveEquilibrium(input: EquilibriumInput): EquilibriumOutcome {
  const { mode, unit } = input;
  const RT = (kelvin: number) => GAS_CONSTANT * kelvin;
  const specs: ReadSpec[] = [
    {
      key: "t",
      text: input.text.t,
      kind: "temperature",
      unit: unit.t,
      factor: 1,
    },
  ];
  if (mode !== "dg0") {
    specs.push({
      key: "dg0",
      text: input.text.dg0,
      kind: "any",
      unit: "",
      factor: unitFactor(MOLAR_ENERGY_UNITS, unit.dg0),
    });
  }
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const kelvin = read.values.t;
  let kRead: { value: number } | GibbsFailure | null = null;
  if (mode === "dg0") {
    kRead = readPositive("k", input.text.k);
  }
  let qRead: { value: number } | GibbsFailure | null = null;
  if (mode === "dg") {
    qRead = readPositive("q", input.text.q);
  }
  for (const r of [kRead, qRead]) {
    if (r && "ok" in r) {
      return r;
    }
  }
  const dg0 =
    mode === "dg0" && kRead && !("ok" in kRead)
      ? -RT(kelvin) * Math.log(kRead.value)
      : read.values.dg0;
  const lnK = -dg0 / RT(kelvin);
  const q = qRead && !("ok" in qRead) ? qRead.value : null;
  const dg = q === null ? null : dg0 + RT(kelvin) * Math.log(q);
  let answer: number;
  if (mode === "k") {
    answer = Math.exp(lnK);
  } else if (mode === "dg0") {
    answer = dg0 / unitFactor(MOLAR_ENERGY_UNITS, unit.dg0);
  } else {
    answer = (dg ?? 0) / unitFactor(MOLAR_ENERGY_UNITS, unit.dg);
  }
  return {
    ok: true,
    mode,
    dg0,
    kelvin,
    log10K: lnK / Math.LN10,
    lnK,
    q,
    dg,
    direction: dg === null ? null : directionOf(dg, Math.abs(dg0)),
    answer,
    answerUnit: mode === "dg" ? unit.dg : unit.dg0,
  };
}

/** K = mantissa × 10^exponent from log₁₀ K, rounded to `sig` significant figures. */
export function splitLog10(
  log10K: number,
  sig: number
): { mantissa: number; exponent: number } {
  let exponent = Math.floor(log10K);
  let mantissa = Number((10 ** (log10K - exponent)).toPrecision(sig));
  if (mantissa >= 10) {
    mantissa /= 10;
    exponent += 1;
  }
  return { mantissa, exponent };
}
